import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import {
    Check,
    AlertTriangle,
    Loader,
    CreditCard,
    FileText,
    X
} from "lucide-react";
import api from "../../api/axios";
import { NotificationContext } from "../../context/NotificationContext";
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../context/AuthContext";
import MembershipCard from "./MembershipCard";


export default function SubscriptionPage() {
    const { user } = useAuth();
    const { addNotification } = useContext(NotificationContext);
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" or "yearly"
    const [processingId, setProcessingId] = useState(null);
    const [showAllPlans, setShowAllPlans] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);

    const [allPlans, setAllPlans] = useState([]);
    const [payments, setPayments] = useState([]);
    const [selectedPaymentForReceipt, setSelectedPaymentForReceipt] = useState(null);

    useEffect(() => {
        fetchSubscriptionData();
    }, []);

    const navigate = useNavigate();

    const fetchSubscriptionData = async () => {
        try {
            setLoading(true);
            const [subRes, plansRes, paymentsRes] = await Promise.all([
                api.get("/api/subscriptions/my-subscription/").catch(() => null),
                api.get("/api/subscriptions/plans/").catch(() => null),
                api.get("/api/subscriptions/payments/").catch(() => null)
            ]);
            setSubscription(subRes?.data || null);
            setAllPlans(plansRes?.data || []);
            setPayments(paymentsRes?.data || []);
        } catch (error) {
            // Optional: suppress if just checking
        } finally {
            setLoading(false);
        }
    };

    const handleUpgrade = (plan) => {
        navigate('/owner/payment', {
            state: {
                plan: {
                    id: plan.id,
                    name: plan.name,
                    price: plan.price,
                    duration_days: plan.duration_days
                }
            }
        });
    };

    const executeCancel = async () => {
        try {
            setProcessingId('cancel');
            await api.post("/api/subscriptions/cancel/");
            addNotification("Subscription cancelled successfully", "success");
            await fetchSubscriptionData();
            setShowAllPlans(true);
        } catch (error) {
            addNotification(error.response?.data?.message || "Failed to cancel subscription", "error");
        } finally {
            setProcessingId(null);
        }
    };

    const handleCancel = () => {
        setConfirmCancel(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const currentPlanId = subscription?.plan_details?.id; // This might differ from our static IDs if backend isn't synced, but that's okay for now.

    // Filter plans based on billing cycle (exclude Free Trial from toggle unless selected explicitly or we want to show it)
    const displayPlans = allPlans.filter(plan => {
        if (!plan.is_active) return false; // Filter out inactive plans
        
        if (plan.code === 'FREE') return true; // Show Trial plan

        if (billingCycle === 'monthly') return plan.duration_days === 30;
        if (billingCycle === 'yearly') return plan.duration_days === 365;
        return false;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader className="w-8 h-8 text-green-600 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-8 p-6 max-w-7xl mx-auto">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col md:flex-row md:items-center justify-between gap-4"
            >
                <div>
                    <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Subscription Plans</h2>
                    <p className="text-slate-500 dark:text-slate-400">Choose the plan that fits your business needs</p>
                </div>

                {/* Billing Cycle Toggle */}
                {(!subscription || showAllPlans) && (
                    <div className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl flex items-center border border-slate-200 dark:border-slate-700">
                        <button
                            onClick={() => setBillingCycle("monthly")}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${billingCycle === "monthly"
                                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                }`}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setBillingCycle("yearly")}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${billingCycle === "yearly"
                                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                }`}
                        >
                            Yearly <span className="text-green-500 text-xs ml-1">(Save ~17%)</span>
                        </button>
                    </div>
                )}
            </motion.div>

            {/* ACTIVE SUBSCRIPTION DASHBOARD */}
            {subscription && !showAllPlans ? (
                <div className="space-y-8">
                    {/* Current Plan Card */}
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                            <h3 className="font-semibold text-slate-900 dark:text-white text-lg">Current plan</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x border-slate-200 dark:border-slate-700">
                            {/* Plan Details */}
                            <div className="p-6">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">PLAN</span>
                                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mt-2 mb-1">
                                    {(subscription.plan_details?.name || "Unknown Plan").replace(/\(Monthly\)|\(Yearly\)/gi, '').trim()}
                                </h2>
                                <button
                                    onClick={() => setShowAllPlans(true)}
                                    className="text-green-600 dark:text-green-400 text-sm font-black hover:underline"
                                >
                                    Upgrade or Change Plan
                                </button>
                            </div>

                            {/* Billing Cycle */}
                            <div className="p-6">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">BILLING CYCLE</span>
                                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mt-2 mb-1">
                                    {subscription.plan_details?.price === 0 || parseFloat(subscription.plan_details?.price) === 0 ? "Free / 1 Year" : ((subscription.plan_details?.duration_days || 0) >= 365 ? "Yearly" : "Monthly")}
                                </h2>
                                {(subscription.plan_details?.duration_days || 0) < 365 && (
                                    <span className="inline-block bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs px-2 py-0.5 rounded font-medium">
                                        Active
                                    </span>
                                )}
                            </div>

                            {/* Billing Date */}
                            <div className="p-6 border-t border-slate-200 dark:border-slate-700 md:border-t-0">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">BILLING DATE</span>
                                <div className="mt-4 flex items-center gap-4">
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Last billing</p>
                                        <p className="font-semibold text-slate-900 dark:text-white">{formatDate(subscription.start_date)}</p>
                                    </div>
                                    <div className="h-px w-8 bg-slate-300 dark:bg-slate-600"></div>
                                    <div>
                                        <p className="text-xs text-slate-500 mb-1">Next billing</p>
                                        <p className="font-semibold text-slate-900 dark:text-white">{formatDate(subscription.end_date)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Licenses */}
                            <div className="p-6 border-t border-slate-200 dark:border-slate-700 md:border-t-0">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">LICENSES</span>
                                <div className="mt-2">
                                    <span className="text-3xl font-bold text-slate-900 dark:text-white">
                                        {subscription.staff_count || 0}
                                        <span className="text-slate-400 text-xl font-normal">
                                            /{subscription.plan_details?.max_staff_users === 0 ? "∞" : subscription.plan_details?.max_staff_users}
                                        </span>
                                    </span>
                                    <span className="text-sm text-slate-500 ml-2">allocated</span>
                                </div>
                                <a href="/owner/team-access" className="text-green-600 dark:text-green-400 text-sm font-black hover:underline block mt-1">
                                    Manage licenses
                                </a>
                            </div>

                            {/* Membership Card - Visual Status */}
                            <div className="p-6 col-span-1 md:col-span-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20 flex flex-col items-center">
                                <div className="max-w-md w-full">
                                    <div className="flex justify-between items-center mb-6">
                                        <div>
                                            <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">Your Digital Membership</h4>
                                            <p className="text-xs text-slate-500">Official status card for your {subscription.plan_details?.name}</p>
                                        </div>
                                        <div className="px-3 py-1 bg-green-100 text-green-700 text-[10px] font-black rounded-lg border border-green-200">
                                            ACTIVE STATUS
                                        </div>
                                    </div>
                                    <div className="flex justify-center">
                                        <MembershipCard 
                                            plan={subscription.plan_details} 
                                            companyName={user?.company_profile?.company_name} 
                                            expiryDate={subscription.end_date}
                                            cardNumber={`4297 6901 17${(user?.id || 10).toString().padStart(2, '0')} ${Math.floor(1000 + Math.random() * 9000)}`}
                                        />
                                    </div>
                                    <p className="text-center text-[10px] text-slate-400 mt-6 italic">
                                        This card serves as your digital proof of membership within the gio billing ecosystem.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Check for "No Active Plan" warning if needed */}
                    {subscription.status !== 'ACTIVE' && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-4">
                            <AlertTriangle className="w-6 h-6 text-red-600" />
                            <div>
                                <h3 className="text-red-900 font-bold">Subscription Inactive</h3>
                                <p className="text-red-700 text-sm mt-1">
                                    Your subscription is currently {subscription.status.toLowerCase()}. Please renew or upgrade to continue using premium features.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Cancel Subscription */}
                    <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-slate-900 dark:text-white">Cancel subscription</h3>
                                <p className="text-slate-500 text-sm mt-1">
                                    Canceled subscription will remain active until the end of the current billing period.
                                </p>
                            </div>
                            <button
                                onClick={() => !user?.is_demo && handleCancel()}
                                disabled={processingId !== null || user?.is_demo}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${user?.is_demo
                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : "bg-red-50 text-red-600 hover:bg-red-100"
                                    }`}
                            >
                                {processingId === 'cancel' ? 'Cancelling...' : 'Cancel subscription'}
                            </button>
                        </div>
                    </div>

                    {/* Payment History Section */}
                    <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3 mb-6">
                            <CreditCard className="w-6 h-6 text-green-600 animate-pulse" />
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Payment History</h3>
                        </div>

                        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-slate-500 text-[10px] font-black uppercase tracking-wider">
                                            <th className="px-6 py-4">Plan</th>
                                            <th className="px-6 py-4">Amount</th>
                                            <th className="px-6 py-4">Payment Method</th>
                                            <th className="px-6 py-4">Status</th>
                                            <th className="px-6 py-4">Date & Time</th>
                                            <th className="px-6 py-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-300">
                                        {payments.map(item => (
                                            <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors">
                                                <td className="px-6 py-4 text-slate-900 dark:text-white font-extrabold text-xs">
                                                    {item.plan_details?.name || 'Standard Plan'}
                                                </td>
                                                <td className="px-6 py-4 font-black">
                                                    ₹{parseFloat(item.amount).toFixed(2)}
                                                </td>
                                                <td className="px-6 py-4 uppercase text-[10px] text-slate-500">
                                                    {item.payment_method || 'Razorpay'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2.5 py-1 text-[10px] font-black uppercase rounded-full tracking-wider border ${
                                                        item.status === 'COMPLETED'
                                                            ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-800'
                                                            : item.status === 'PENDING'
                                                            ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-800'
                                                            : 'bg-red-50 text-red-750 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-800'
                                                    }`}>
                                                        {item.status === 'COMPLETED' ? 'Successful' : item.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-slate-550 dark:text-slate-450">
                                                    {new Date(item.created_at).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <button
                                                        onClick={() => setSelectedPaymentForReceipt(item)}
                                                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-650 text-slate-700 dark:text-slate-200 rounded-lg text-[10px] font-black tracking-wider uppercase transition flex items-center gap-1.5"
                                                    >
                                                        <FileText className="w-3.5 h-3.5" />
                                                        Receipt
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {payments.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="px-6 py-12 text-center text-slate-450 font-bold">
                                                    No payment history records found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                /* PLANS LIST */
                <div className="space-y-6">
                    {showAllPlans && (
                        <button
                            onClick={() => setShowAllPlans(false)}
                            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                        >
                            ← Back to my plan
                        </button>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {displayPlans.map((plan, idx) => {
                            const isCurrent = currentPlanId === plan.id; // Note: IDs might not match exactly unless backend matches static file.

                            return (
                                <motion.div
                                    key={plan.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className={`relative bg-white dark:bg-slate-800 rounded-2xl p-6 border transition-all hover:shadow-xl flex flex-col ${isCurrent
                                        ? "border-green-500 ring-4 ring-green-500/10 z-10"
                                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300"
                                        }`}
                                >
                                    <div className="mb-4">
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className={`text-xl font-bold ${isCurrent ? 'text-green-600 dark:text-green-400' : 'text-slate-900 dark:text-white'}`}>
                                                {plan.name.replace(/\(Monthly\)|\(Yearly\)/gi, '').trim()}
                                            </h3>
                                            {plan.id === 'free_trial' && <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-1 rounded-full font-bold">New</span>}
                                        </div>
                                        <p className="text-slate-500 dark:text-slate-400 text-xs mt-1 min-h-[40px] leading-relaxed">
                                            {plan.description}
                                        </p>
                                    </div>

                                    <div className="mb-6 pb-6 border-b border-slate-100 dark:border-slate-700">
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-3xl font-bold text-slate-900 dark:text-white">
                                                {parseFloat(plan.price) === 0 ? "Free" : `₹${plan.price}`}
                                            </span>
                                            <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                                                {parseFloat(plan.price) === 0 
                                                    ? ` / ${plan.duration_days >= 365 ? Math.floor(plan.duration_days / 365) + (plan.duration_days === 365 ? " Year" : " Years") : plan.duration_days + " Days"}`
                                                    : `/${plan.duration_days >= 365 ? "year" : "mo"}`}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-400 mt-2">
                                            + GST as applicable
                                        </div>
                                    </div>

                                    {/* Limits */}
                                    <div className="grid grid-cols-2 gap-2 mb-6 text-xs text-slate-600 dark:text-slate-300">
                                        <div className="bg-slate-50 dark:bg-slate-900/50 p-2 rounded">
                                            <span className="block text-slate-400 text-[10px] uppercase">Users</span>
                                            <span className="font-semibold">{plan.max_staff_users === 0 ? "Unlimited" : plan.max_staff_users} Staff</span>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-900/50 p-2 rounded">
                                            <span className="block text-slate-400 text-[10px] uppercase">Invoices</span>
                                            <span className="font-semibold">{plan.invoice_limit}</span>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-900/50 p-2 rounded">
                                            <span className="block text-slate-400 text-[10px] uppercase">Products</span>
                                            <span className="font-semibold">{plan.product_limit}</span>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-900/50 p-2 rounded">
                                            <span className="block text-slate-400 text-[10px] uppercase">Customers</span>
                                            <span className="font-semibold">{plan.customer_limit}</span>
                                        </div>
                                    </div>

                                    <div className="space-y-3 mb-8 flex-1">
                                        {(Array.isArray(plan.features) ? plan.features : []).map((feature, i) => (
                                            <div key={i} className="flex items-start gap-2">
                                                <div className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-500/10 flex items-center justify-center">
                                                    <Check className="w-2.5 h-2.5 text-emerald-600 dark:text-emerald-400" />
                                                </div>
                                                <span className="text-slate-600 dark:text-slate-300 text-xs font-medium leading-tight">
                                                    {feature}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => !isCurrent && !user?.is_demo && handleUpgrade(plan)}
                                        disabled={isCurrent || processingId !== null || user?.is_demo}
                                        className={`w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-sm ${isCurrent
                                            ? "bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-default"
                                            : user?.is_demo
                                                ? "bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed"
                                                : "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg hover:-translate-y-0.5"
                                            } ${processingId !== null && processingId !== plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {processingId === plan.id ? (
                                            <Loader className="w-4 h-4 animate-spin" />
                                        ) : isCurrent ? (
                                            <>
                                                <Check className="w-4 h-4" /> Current Plan
                                            </>
                                        ) : (
                                            <>
                                                Select Plan
                                            </>
                                        )}
                                    </button>
                                </motion.div>
                            );
                        })}
                    </div>
                    <p className="text-center text-slate-400 text-xs mt-8">
                        Prices are exclusive of GST. Plans can be upgraded anytime.
                    </p>
                </div>
            )}

            {/* Payment Receipt Modal */}
            {selectedPaymentForReceipt && (
                <div className="fixed inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-[70] p-4 transition-all" onClick={() => setSelectedPaymentForReceipt(null)}>
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl transition-colors"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Receipt Header */}
                        <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 flex justify-between items-center transition-colors">
                            <div>
                                <h3 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-wider">Payment Receipt</h3>
                                <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5 font-bold">
                                    Receipt #: REC-SUB-{selectedPaymentForReceipt.id}
                                </p>
                            </div>
                            <button
                                onClick={() => setSelectedPaymentForReceipt(null)}
                                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-750 text-slate-500 rounded-full transition"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Receipt Body */}
                        <div className="p-8 max-h-[70vh] overflow-y-auto space-y-6 text-slate-850 dark:text-slate-200">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className="font-extrabold text-sm text-slate-900 dark:text-white uppercase tracking-tight">Billing To</h4>
                                    <p className="text-xs font-bold text-slate-800 mt-1">{user?.company_profile?.company_name || 'Your Company'}</p>
                                    <p className="text-xs text-slate-500 mt-0.5">{user?.email}</p>
                                </div>
                                <div className="text-right">
                                    <h4 className="font-extrabold text-sm text-slate-900 dark:text-white uppercase tracking-tight">Receipt Details</h4>
                                    <p className="text-xs text-slate-500 mt-1">Date: {new Date(selectedPaymentForReceipt.created_at).toLocaleString()}</p>
                                    <p className="text-xs text-slate-500 mt-0.5">Status: <span className="font-extrabold text-emerald-600 uppercase">{selectedPaymentForReceipt.status}</span></p>
                                </div>
                            </div>

                            <div className="border-t border-b border-slate-100 dark:border-slate-700 py-4 my-4">
                                <div className="flex justify-between font-bold text-xs uppercase text-slate-400 mb-2.5">
                                    <span>Description</span>
                                    <span>Amount</span>
                                </div>
                                <div className="flex justify-between text-xs font-extrabold text-slate-800 dark:text-white">
                                    <span>
                                        {selectedPaymentForReceipt.plan_details?.name || 'Standard Plan'} Subscription
                                    </span>
                                    <span>₹{(parseFloat(selectedPaymentForReceipt.amount) / 1.18).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-xs text-slate-500 mt-1">
                                    <span>CGST (9%)</span>
                                    <span>₹{(parseFloat(selectedPaymentForReceipt.amount) * 0.09 / 1.18).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-xs text-slate-500 mt-1">
                                    <span>SGST (9%)</span>
                                    <span>₹{(parseFloat(selectedPaymentForReceipt.amount) * 0.09 / 1.18).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm font-black text-slate-900 dark:text-white border-t border-slate-150 dark:border-slate-700 pt-3.5 mt-3.5">
                                    <span className="text-purple-600">GRAND TOTAL (incl. GST)</span>
                                    <span className="text-purple-600">₹{parseFloat(selectedPaymentForReceipt.amount).toFixed(2)}</span>
                                </div>
                            </div>

                            {/* Metadata details */}
                            <div className="p-4 bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-slate-150 dark:border-slate-700/60 space-y-2.5 font-mono text-[10px]">
                                <p className="font-bold text-xs text-slate-800 dark:text-white uppercase tracking-widest font-sans mb-1">Transaction Metadata</p>
                                <div className="grid grid-cols-3 gap-1">
                                    <span className="text-slate-500">Payment ID:</span>
                                    <span className="col-span-2 text-slate-800 dark:text-slate-200 truncate">{selectedPaymentForReceipt.razorpay_payment_id || 'N/A'}</span>
                                </div>
                                <div className="grid grid-cols-3 gap-1">
                                    <span className="text-slate-500">Order ID:</span>
                                    <span className="col-span-2 text-slate-800 dark:text-slate-200 truncate">{selectedPaymentForReceipt.razorpay_order_id || 'N/A'}</span>
                                </div>
                                <div className="grid grid-cols-3 gap-1">
                                    <span className="text-slate-500">UTR / Ref ID:</span>
                                    <span className="col-span-2 text-slate-800 dark:text-slate-200 truncate">{selectedPaymentForReceipt.transaction_id || 'N/A'}</span>
                                </div>
                                <div className="grid grid-cols-3 gap-1">
                                    <span className="text-slate-500">Method:</span>
                                    <span className="col-span-2 text-slate-800 dark:text-slate-200 uppercase">{selectedPaymentForReceipt.payment_method || 'Razorpay'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Receipt Footer */}
                        <div className="p-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 flex justify-between items-center transition-colors">
                            <button
                                onClick={() => window.print()}
                                className="px-4 py-2 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-bold transition flex items-center gap-1.5"
                            >
                                <FileText className="w-3.5 h-3.5" />
                                Print Receipt
                            </button>
                            <button
                                onClick={() => setSelectedPaymentForReceipt(null)}
                                className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-bold transition-all"
                            >
                                Done
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Custom Cancellation Confirmation Modal */}
            {confirmCancel && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <motion.div 
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white dark:bg-dark-card rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden border border-gray-100 dark:border-dark-border"
                    >
                        <div className="p-6 text-center">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Confirm Cancellation</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 font-medium">
                                Are you sure you want to cancel? This action cannot be undone.
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setConfirmCancel(false)}
                                    className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-dark-border dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl transition-all cursor-pointer text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        executeCancel();
                                        setConfirmCancel(false);
                                    }}
                                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 cursor-pointer text-sm"
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
